import apiClient from './api'
import type {
  AdminDeviceTemplateDetail,
  AdminDeviceTemplateListItem,
  CreateDeviceCatalogTokenRequest,
  DeviceCatalogDeviceAdmin,
  DeviceTemplate,
  DeviceCatalogSnapshot,
  DeviceCatalogToken,
  DeviceCatalogTokenCreated,
  ImportDeviceCatalogRequest,
  ImportDeviceCatalogResult,
  UpdateDeviceCatalogDeviceRequest,
  UpdateDeviceCatalogFunctionRequest,
} from '@/types/deviceCatalog'

export const deviceCatalogApi = {
  importDevices: async (data: ImportDeviceCatalogRequest) => {
    const response = await apiClient.post<ImportDeviceCatalogResult>(
      '/ai/device-catalog/admin/import',
      data
    )
    return response.data
  },

  listDevices: async (params?: {
    environment?: string
    approved?: boolean
    deviceType?: string
    status?: string
    keyword?: string
    tags?: string[]
    includeInactive?: boolean
    sourceActive?: boolean
    editorPlaceable?: boolean
    userVisible?: boolean
    deviceGroup?: string
    page?: number
    pageSize?: number
  }) => {
    const response = await apiClient.get<{
      items: DeviceCatalogDeviceAdmin[]
      total: number
      page: number
      page_size: number
    }>('/ai/device-catalog/admin/devices', {
      params: {
        environment: params?.environment,
        approved: params?.approved,
        device_type: params?.deviceType,
        status: params?.status,
        keyword: params?.keyword,
        tags: params?.tags?.join(','),
        include_inactive: params?.includeInactive,
        source_active: params?.sourceActive,
        editor_placeable: params?.editorPlaceable,
        user_visible: params?.userVisible,
        device_group: params?.deviceGroup,
        page: params?.page,
        page_size: params?.pageSize,
      },
    })
    return response.data
  },

  listTemplates: async (params?: { enabled?: boolean; category?: string }) => {
    const response = await apiClient.get<{
      items: AdminDeviceTemplateListItem[]
      total: number
    }>('/ai/device-catalog/admin/templates', { params })
    return response.data
  },

  getTemplate: async (templateId: string) => {
    const response = await apiClient.get<AdminDeviceTemplateDetail>(
      `/ai/device-catalog/admin/templates/${templateId}`
    )
    return response.data
  },

  saveTemplate: async (template: DeviceTemplate) => {
    const response = await apiClient.put<DeviceTemplate>(
      `/ai/device-catalog/admin/templates/${template.id}`,
      template
    )
    return response.data
  },

  deleteTemplate: async (templateId: string) => {
    const response = await apiClient.delete<{ template_id: string; deleted: boolean }>(
      `/ai/device-catalog/admin/templates/${templateId}`
    )
    return response.data
  },

  updateDevice: async (deviceId: string, data: UpdateDeviceCatalogDeviceRequest) => {
    const response = await apiClient.patch<DeviceCatalogDeviceAdmin>(
      `/ai/device-catalog/admin/devices/${deviceId}`,
      data
    )
    return response.data
  },

  updateFunction: async (functionId: number, data: UpdateDeviceCatalogFunctionRequest) => {
    const response = await apiClient.patch(`/ai/device-catalog/admin/functions/${functionId}`, data)
    return response.data
  },

  listTokens: async () => {
    const response = await apiClient.get<{
      items: DeviceCatalogToken[]
      total: number
    }>('/ai/device-catalog/tokens')
    return response.data
  },

  listSnapshots: async (params?: { environment?: string; venueId?: number }) => {
    const response = await apiClient.get<{
      items: DeviceCatalogSnapshot[]
      total: number
      latest: DeviceCatalogSnapshot | null
    }>('/ai/device-catalog/admin/snapshots', {
      params: {
        environment: params?.environment,
        venue_id: params?.venueId,
      },
    })
    return response.data
  },

  createToken: async (data: CreateDeviceCatalogTokenRequest) => {
    const response = await apiClient.post<DeviceCatalogTokenCreated>(
      '/ai/device-catalog/tokens',
      data
    )
    return response.data
  },

  revokeToken: async (tokenId: string) => {
    const response = await apiClient.post<{ token_id: string; status: string }>(
      `/ai/device-catalog/tokens/${tokenId}/revoke`
    )
    return response.data
  },
}
